import { useEffect, useState, useCallback } from "react";
import Breadcrumbs from "../../../components/Breadcrumbs";
import ResizableEditArea from "../../../components/common/ResizableEditArea";
import { MESSAGE_TYPES } from "../../../utils/editIframe";
import { useDragAndDrop } from "../../../hooks/useDragAndDrop";
import StatusMessage from "../../../components/common/StatusMessage";
import Iframe from "../../../components/Iframe";
import {
  clearValidationErrors,
  executeValidationWithArrays,
  handleArrayInputChangeWithValidation,
  handleArrayItemRemoveWithValidation,
  handleResetWithValidation,
} from "../../../utils/formValidation";

const breadcrumbs = [
  { title: "Home", path: `/` },
  { title: "Privacy", path: `/privacy` },
];

export default function PrivacyEditPage() {
  const contentKey = "privacyContent";
  const [message, setMessage] = useState("");
  const [loadingText, setLoadingText] = useState("");
  const [pageContent, setPageContent] = useState({ privacy_items: [] });

  const handlePrivacyItemReorder = useCallback(
    (reorderedItems) => {
      setPageContent((prev) => {
        const newData = { ...prev, privacy_items: reorderedItems };
        clearValidationErrors();
        const iframe = document.querySelector("iframe");
        if (iframe?.contentWindow) {
          iframe.contentWindow.postMessage(
            { type: MESSAGE_TYPES.TEXT_UPDATE, contentKey, allData: newData, isFullUpdate: true },
            process.env.REACT_APP_ORIGIN
          );
        }
        return newData;
      });
    },
    [contentKey]
  );

  const { getDraggableProps } = useDragAndDrop(
    pageContent.privacy_items || [],
    handlePrivacyItemReorder
  );

  const fetchPageContentData = useCallback(async () => {
    setLoadingText("読み込み中");
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_page_privacy_content/`);
      if (!response.ok) throw new Error("ネットワークレスポンスが異常です");
      const data = await response.json();
      if (!data.privacy_items?.length) {
        data.privacy_items = [{ title: "", content: "" }];
      }
      setPageContent(data);
    } catch (error) {
      console.error("データ取得エラー:", error);
    }
    setLoadingText("");
  }, []);

  useEffect(() => {
    fetchPageContentData();
  }, [fetchPageContentData]);

  const arrayValidationRules = {
    privacy_items: {
      content: { required: true },
    },
  };

  const fieldLabels = {};

  const handlePageDataSubmit = async () => {
    if (
      !executeValidationWithArrays(
        pageContent,
        {},
        arrayValidationRules,
        fieldLabels,
        setMessage
      )
    ) {
      return;
    }

    setLoadingText("保存中");
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/update_page_privacy_content/`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ page_content: pageContent }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message);
      setMessage(data.message);
      await fetchPageContentData();
      setTimeout(() => {
        const editArea = document.querySelector(".editarea");
        if (editArea) editArea.scrollTop = 0;
      }, 100);
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("更新エラー:", error);
      setMessage("更新に失敗しました");
      setTimeout(() => setMessage(""), 3000);
    }
    setLoadingText("");
  };

  const handleReset = () => {
    handleResetWithValidation(fetchPageContentData);
  };

  const handlePrivacyItemInputChange = (e, index, fieldName) => {
    handleArrayInputChangeWithValidation(
      e,
      index,
      fieldName,
      setPageContent,
      "privacy_items",
      (newData, index, fieldName, value, eventType) => {
        const iframe = document.querySelector("iframe");
        if (iframe?.contentWindow) {
          const targetClass = `.edit_privacy_item${index}_${fieldName}`;
          const messageType =
            eventType === "focus" ? MESSAGE_TYPES.SCROLL_TO : MESSAGE_TYPES.TEXT_UPDATE;
          iframe.contentWindow.postMessage(
            {
              type: messageType,
              target: targetClass,
              contentKey,
              fieldName,
              index,
              value,
              allData: newData,
            },
            process.env.REACT_APP_ORIGIN
          );
        }
      }
    );
  };

  const addPrivacyItem = (insertAfterIndex = null) => {
    setPageContent((prev) => {
      const newItem = { title: "", content: "" };
      let newItems;
      if (insertAfterIndex !== null) {
        newItems = [
          ...prev.privacy_items.slice(0, insertAfterIndex + 1),
          newItem,
          ...prev.privacy_items.slice(insertAfterIndex + 1),
        ];
      } else {
        newItems = [...prev.privacy_items, newItem];
      }
      const newData = { ...prev, privacy_items: newItems };
      const iframe = document.querySelector("iframe");
      if (iframe?.contentWindow) {
        iframe.contentWindow.postMessage(
          { type: MESSAGE_TYPES.TEXT_UPDATE, contentKey, allData: newData },
          process.env.REACT_APP_ORIGIN
        );
      }
      return newData;
    });
  };

  const removePrivacyItem = (index) => {
    if (pageContent.privacy_items.length <= 1) return;
    handleArrayItemRemoveWithValidation(
      index,
      setPageContent,
      "privacy_items",
      arrayValidationRules.privacy_items,
      fieldLabels,
      (newData) => {
        const iframe = document.querySelector("iframe");
        if (iframe?.contentWindow) {
          iframe.contentWindow.postMessage(
            { type: MESSAGE_TYPES.TEXT_UPDATE, contentKey, allData: newData },
            process.env.REACT_APP_ORIGIN
          );
        }
      }
    );
  };

  useEffect(() => {
    const styleElement = document.createElement("style");
    styleElement.textContent = `.loop { grid-template-columns: auto !important; }`;
    document.head.appendChild(styleElement);
    return () => document.head.removeChild(styleElement);
  }, []);

  return (
    <>
      <ResizableEditArea>
        {({ onResizeStart }) => (
          <>
            <Breadcrumbs breadcrumbs={breadcrumbs} onResizeStart={onResizeStart} />
            <StatusMessage message={message} type="success" />

            <div className="form">
              <section>
                <div className="area2">
                  {pageContent.privacy_items?.map((item, index) => (
                    <div className="item" key={index} {...getDraggableProps(index)}>
                      <div className="ctrl">
                        <p>{index + 1}</p>
                        <div className="icon-wrap">
                          <i className="icon delete" onClick={() => removePrivacyItem(index)}></i>
                          <i className="icon add" onClick={() => addPrivacyItem(index)}></i>
                        </div>
                      </div>
                      <div className="forms loop" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="text"
                          name={`privacy_item${index}_title`}
                          value={pageContent.privacy_items[index]?.title ?? ""}
                          onChange={(e) => handlePrivacyItemInputChange(e, index, "title")}
                          onFocus={(e) => handlePrivacyItemInputChange(e, index, "title")}
                          placeholder="タイトル（例：１．個人情報の定義）"
                          data-label="タイトル"
                        />
                        <textarea
                          className="input2"
                          name={`privacy_item${index}_content`}
                          value={pageContent.privacy_items[index]?.content ?? ""}
                          onChange={(e) => handlePrivacyItemInputChange(e, index, "content")}
                          onFocus={(e) => handlePrivacyItemInputChange(e, index, "content")}
                          placeholder="内容"
                          rows={6}
                          data-label="内容"
                          data-required="true"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </div>

            <div className="submit-area">
              <button type="button" onClick={handleReset}>
                Reset
              </button>
              <button type="button" onClick={handlePageDataSubmit}>
                Submit
              </button>
            </div>
          </>
        )}
      </ResizableEditArea>

      {loadingText && (
        <div className="overlay">
          <div className="loading">
            <div className="circle"></div>
            <p>{loadingText}...</p>
          </div>
        </div>
      )}

      <Iframe path="/privacy" data={pageContent} contentKey={contentKey} />
    </>
  );
}
